/**
 * SocialImageGenerator - AI-powered social media image generation
 * Clean interface with left sidebar for templates/options, right side for chat
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Sparkles, Download, Trash2, Loader2, AlertCircle,
    Send, X, Wand2, Copy, CheckCircle, Settings2,
    Crown, MessageSquare, ArrowLeft, GitBranch,
    History, ChevronRight, ChevronDown, PanelLeftClose, PanelLeft,
    BarChart3, TrendingUp, Quote, ListChecks, ArrowLeftRight,
    Layers, Megaphone, HelpCircle, Smile, MousePointerClick
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import PropTypes from 'prop-types';
import {
    IMAGE_STYLES,
    ASPECT_RATIOS,
    CREDIT_COSTS,
    CONTENT_FORMATS,
    PLATFORM_PRESETS,
    PROMPT_BOOSTERS,
    generateAndPoll,
    downloadImage,
    getStyleById,
    getImageUrl,
    normalizeGeneratedImage,
    buildPostComposerSeed,
    savePostComposerSeed
} from '../../services/SocialImageService';
import { fetchCreditBalance } from '../../services/ChatUsageService';

// Constants
const SESSION_STATUS = { ACTIVE: 'active', COMPLETED: 'completed' };

// Gallery images for Pro service
const GALLERY_IMAGES = [
    { id: 1, src: '/gallery/post1_fb_TA.jpg', title: 'Mortgage Tips' },
    { id: 2, src: '/gallery/post4_fb_TA.jpg', title: 'Home Buying Guide' },
    { id: 3, src: '/gallery/post7_fb_TA.jpg', title: 'Rate Update' },
    { id: 4, src: '/gallery/post9_fb_TA.jpg', title: 'Client Success' },
    { id: 5, src: '/gallery/post10_fb_TA.jpg', title: 'Market Insight' },
    { id: 6, src: '/gallery/post11_fb_TA.jpg', title: 'First-Time Buyer' },
    { id: 7, src: '/gallery/post12_fb_TA.jpg', title: 'Just Closed' },
    { id: 8, src: '/gallery/post14_fb_TA.jpg', title: 'Professional Promo' },
];

const DESIGNER_PACKAGES = [
    { id: 'single', images: 1, price: 19.99 },
    { id: 'starter', images: 3, price: 49.99 },
    { id: 'standard', images: 5, price: 79.99, popular: true },
    { id: 'pro', images: 10, price: 149.99 },
];

// Icon mapping for content formats
const FORMAT_ICONS = {
    'infographic': BarChart3,
    'stat-highlight': TrendingUp,
    'quote-card': Quote,
    'listicle': ListChecks,
    'comparison': ArrowLeftRight,
    'carousel-cover': Layers,
    'announcement': Megaphone,
    'question': HelpCircle,
    'meme-style': Smile,
    'cta-banner': MousePointerClick
};

// Helper functions
const formatCredits = (credits) => {
    if (credits === null || credits === undefined) return '...';
    if (credits >= 1000) return `${(credits / 1000).toFixed(1)}k`;
    return credits.toLocaleString();
};

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

// Group presets by category
const groupedPresets = PLATFORM_PRESETS.reduce((acc, preset) => {
    const cat = preset.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(preset);
    return acc;
}, {});

const CATEGORY_LABELS = {
    engagement: 'High Engagement',
    trust: 'Build Trust',
    conversion: 'Drive Conversions',
    awareness: 'Brand Awareness'
};

// ============================================================
// CHAT MESSAGE COMPONENT
// ============================================================
const ChatMessage = ({ message, onDownload, onUseInPost, onBranchFrom }) => {
    const isUser = message.role === 'user';
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        if (message.image_url) {
            await navigator.clipboard.writeText(message.image_url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    if (isUser) {
        return (
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex justify-end"
            >
                <div className="max-w-[85%] px-4 py-3 bg-teal-500 text-white rounded-2xl rounded-br-sm">
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                </div>
            </motion.div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex gap-3"
        >
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center flex-shrink-0">
                <Wand2 className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 space-y-3">
                {message.content && (
                    <p className="text-sm text-slate-600 dark:text-slate-300">{message.content}</p>
                )}
                {message.image_url && (
                    <div className="relative group inline-block">
                        <img
                            src={message.image_url}
                            alt={`Version ${message.version}`}
                            className="max-w-full rounded-xl shadow-lg"
                            style={{ maxHeight: '400px' }}
                        />
                        <div className="absolute top-2 left-2">
                            <span className="px-2 py-1 bg-black/60 backdrop-blur-sm text-white text-xs rounded-lg font-medium">
                                v{message.version}
                            </span>
                        </div>
                        <div className="absolute bottom-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                                onClick={() => onDownload(message.image_url, `social-image-v${message.version}.png`)}
                                className="p-2 bg-black/60 hover:bg-black/80 backdrop-blur-sm text-white rounded-lg transition-colors"
                                title="Download"
                            >
                                <Download className="w-4 h-4" />
                            </button>
                            <button
                                onClick={handleCopy}
                                className="p-2 bg-black/60 hover:bg-black/80 backdrop-blur-sm text-white rounded-lg transition-colors"
                                title="Copy URL"
                            >
                                {copied ? <CheckCircle className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                            </button>
                            {onUseInPost && (
                                <button
                                    onClick={() => onUseInPost(message.image_url)}
                                    className="p-2 bg-black/60 hover:bg-black/80 backdrop-blur-sm text-white rounded-lg transition-colors"
                                    title="Use in Post"
                                >
                                    <Send className="w-4 h-4" />
                                </button>
                            )}
                            {onBranchFrom && message.version > 0 && (
                                <button
                                    onClick={() => onBranchFrom(message.version)}
                                    className="p-2 bg-black/60 hover:bg-black/80 backdrop-blur-sm text-white rounded-lg transition-colors"
                                    title="Branch from this version"
                                >
                                    <GitBranch className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </motion.div>
    );
};

ChatMessage.propTypes = {
    message: PropTypes.object.isRequired,
    onDownload: PropTypes.func.isRequired,
    onUseInPost: PropTypes.func,
    onBranchFrom: PropTypes.func
};

// ============================================================
// SESSION CARD COMPONENT
// ============================================================
const SessionCard = ({ session, onOpen, onDelete }) => {
    const latestImage = session.messages?.find(m => m.role === 'assistant' && m.image_url);

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden hover:shadow-lg transition-all cursor-pointer group"
            onClick={() => onOpen(session.session_id)}
        >
            {latestImage?.image_url && (
                <div className="aspect-video bg-slate-100 dark:bg-slate-700 overflow-hidden">
                    <img src={latestImage.image_url} alt="" className="w-full h-full object-cover" />
                </div>
            )}
            <div className="p-3">
                <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium text-slate-800 dark:text-white line-clamp-2">
                        {session.caption || 'Untitled'}
                    </p>
                    <button
                        onClick={(e) => { e.stopPropagation(); onDelete(session.session_id); }}
                        className="p-1 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
                <p className="text-xs text-slate-400 mt-1">
                    v{session.current_version || 1} · {formatDate(session.updated_at)}
                </p>
            </div>
        </motion.div>
    );
};

SessionCard.propTypes = {
    session: PropTypes.object.isRequired,
    onOpen: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired
};

// ============================================================
// MAIN COMPONENT
// ============================================================
const SocialImageGenerator = ({ currentBrandId, onUseInPostRequest }) => {
    // Sidebar state
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [expandedCategory, setExpandedCategory] = useState('engagement');

    // Tab state
    const [activeTab, setActiveTab] = useState('chat');

    // Form state
    const [inputValue, setInputValue] = useState('');
    const [style, setStyle] = useState('modern');
    const [aspectRatio, setAspectRatio] = useState('1:1');
    const [contentFormat, setContentFormat] = useState('');
    const [activeBoosters, setActiveBoosters] = useState([]);

    // Session state
    const [sessions, setSessions] = useState([]);
    const [activeSession, setActiveSession] = useState(null);
    const [sessionMessages, setSessionMessages] = useState([]);
    const [branchFromVersion, setBranchFromVersion] = useState(null);

    // Generation state
    const [isGenerating, setIsGenerating] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState(null);

    // Gallery state
    const [selectedGalleryImage, setSelectedGalleryImage] = useState(null);

    // Credits
    const [credits, setCredits] = useState(null);

    const inputRef = useRef(null);

    // Load credits
    useEffect(() => {
        const loadCredits = async () => {
            try {
                if (typeof fetchCreditBalance === 'function') {
                    const res = await fetchCreditBalance();
                    setCredits(res?.balance ?? res?.credits ?? res?.credits_balance ?? 0);
                }
            } catch (err) {
                console.error('Failed to load credits:', err);
            }
        };
        loadCredits();
    }, []);

    // Load sessions from localStorage
    const loadSessions = useCallback(() => {
        try {
            const saved = localStorage.getItem('social_image_sessions');
            setSessions(saved ? JSON.parse(saved) : []);
        } catch {
            setSessions([]);
        }
    }, []);

    useEffect(() => { loadSessions(); }, [loadSessions]);

    // Save sessions
    const saveSessions = useCallback((newSessions) => {
        localStorage.setItem('social_image_sessions', JSON.stringify(newSessions));
        setSessions(newSessions);
    }, []);

    // Scroll chat to bottom - use scrollTop instead of scrollIntoView to avoid affecting other containers
    const chatContainerRef = useRef(null);
    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [sessionMessages]);

    // Generate demo image URL
    const generateDemoImageUrl = (text, version = 1) => {
        const colors = ['667eea', '764ba2', 'f093fb', 'f5576c', '4facfe'];
        const color = colors[version % colors.length];
        return `https://placehold.co/1080x1080/${color}/ffffff?text=${encodeURIComponent(text.slice(0, 20))}`;
    };

    // Build structured prompt from selections
    const buildStructuredPrompt = useCallback((userMessage) => {
        let prompt = userMessage;

        // Add content format instructions
        if (contentFormat) {
            const format = CONTENT_FORMATS.find(f => f.id === contentFormat);
            if (format) {
                prompt += `\n\nFormat: ${format.name}. ${format.promptHint}`;
            }
        }

        // Add style
        const styleObj = IMAGE_STYLES.find(s => s.id === style);
        if (styleObj) {
            prompt += `\n\nStyle: ${styleObj.name} - ${styleObj.description}`;
        }

        // Add boosters
        if (activeBoosters.length > 0) {
            const boosterTexts = activeBoosters.map(id => {
                const booster = PROMPT_BOOSTERS.find(b => b.id === id);
                return booster?.text;
            }).filter(Boolean);
            if (boosterTexts.length > 0) {
                prompt += '\n\n' + boosterTexts.join(' ');
            }
        }

        return prompt;
    }, [contentFormat, style, activeBoosters]);

    // Apply preset
    const applyPreset = useCallback((preset) => {
        setInputValue(preset.caption + '\n\n' + preset.instructions);
        setStyle(preset.style);
        setAspectRatio(preset.aspectRatio);
        setContentFormat(preset.format);
        inputRef.current?.focus();
    }, []);

    // Toggle booster
    const toggleBooster = useCallback((id) => {
        setActiveBoosters(prev =>
            prev.includes(id) ? prev.filter(b => b !== id) : [...prev, id]
        );
    }, []);

    // Handle send message
    const handleSend = useCallback(async () => {
        const message = inputValue.trim();
        if (!message || isGenerating) return;

        const structuredPrompt = buildStructuredPrompt(message);

        setError(null);
        setIsGenerating(true);
        setProgress(0);
        setInputValue('');

        const interval = setInterval(() => setProgress(p => Math.min(p + Math.random() * 8, 95)), 300);

        try {
            if (!activeSession) {
                const result = await generateAndPoll(
                    { caption: message, style, instructions: structuredPrompt, aspectRatio, brandId: currentBrandId },
                    (p) => setProgress(p)
                );
                clearInterval(interval);
                setProgress(100);

                const image = normalizeGeneratedImage(result);
                const imageUrl = getImageUrl(image) || generateDemoImageUrl(message, 1);

                const newSession = {
                    session_id: `session-${Date.now()}`,
                    caption: message,
                    style,
                    aspect_ratio: aspectRatio,
                    content_format: contentFormat,
                    current_version: 1,
                    status: SESSION_STATUS.ACTIVE,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                    messages: [
                        { role: 'user', content: message, created_at: new Date().toISOString() },
                        { role: 'assistant', content: 'Here\'s your image:', image_url: imageUrl, version: 1, created_at: new Date().toISOString() }
                    ]
                };

                saveSessions([newSession, ...sessions]);
                setActiveSession(newSession);
                setSessionMessages(newSession.messages);
                setCredits(prev => prev - CREDIT_COSTS.social_image);
            } else {
                const userMsg = {
                    role: 'user',
                    content: branchFromVersion ? `[From v${branchFromVersion}] ${message}` : message,
                    created_at: new Date().toISOString()
                };
                setSessionMessages(prev => [...prev, userMsg]);

                const result = await generateAndPoll(
                    { caption: activeSession.caption, style: activeSession.style, instructions: structuredPrompt, aspectRatio: activeSession.aspect_ratio, brandId: currentBrandId },
                    (p) => setProgress(p)
                );
                clearInterval(interval);

                const newVersion = (activeSession.current_version || 1) + 1;
                const image = normalizeGeneratedImage(result);
                const imageUrl = getImageUrl(image) || generateDemoImageUrl(message, newVersion);

                const assistantMsg = {
                    role: 'assistant',
                    content: branchFromVersion ? `Branched from v${branchFromVersion}:` : 'Here\'s the updated version:',
                    image_url: imageUrl,
                    version: newVersion,
                    created_at: new Date().toISOString()
                };

                const updatedMessages = [...sessionMessages, userMsg, assistantMsg];
                const updatedSession = { ...activeSession, current_version: newVersion, messages: updatedMessages, updated_at: new Date().toISOString() };

                setSessionMessages(updatedMessages);
                setActiveSession(updatedSession);
                saveSessions(sessions.map(s => s.session_id === activeSession.session_id ? updatedSession : s));
                setBranchFromVersion(null);
                setCredits(prev => prev - CREDIT_COSTS.social_image);
            }
        } catch (err) {
            clearInterval(interval);
            // Demo fallback
            const imageUrl = generateDemoImageUrl(message, activeSession ? (activeSession.current_version || 1) + 1 : 1);

            if (!activeSession) {
                const newSession = {
                    session_id: `session-${Date.now()}`,
                    caption: message,
                    style,
                    aspect_ratio: aspectRatio,
                    content_format: contentFormat,
                    current_version: 1,
                    status: SESSION_STATUS.ACTIVE,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                    messages: [
                        { role: 'user', content: message, created_at: new Date().toISOString() },
                        { role: 'assistant', content: 'Here\'s your image:', image_url: imageUrl, version: 1, created_at: new Date().toISOString() }
                    ]
                };
                saveSessions([newSession, ...sessions]);
                setActiveSession(newSession);
                setSessionMessages(newSession.messages);
            } else {
                const newVersion = (activeSession.current_version || 1) + 1;
                const userMsg = { role: 'user', content: message, created_at: new Date().toISOString() };
                const assistantMsg = { role: 'assistant', content: 'Here\'s the updated version:', image_url: imageUrl, version: newVersion, created_at: new Date().toISOString() };
                const updatedMessages = [...sessionMessages, userMsg, assistantMsg];
                const updatedSession = { ...activeSession, current_version: newVersion, messages: updatedMessages, updated_at: new Date().toISOString() };

                setSessionMessages(updatedMessages);
                setActiveSession(updatedSession);
                saveSessions(sessions.map(s => s.session_id === activeSession.session_id ? updatedSession : s));
            }
        } finally {
            clearInterval(interval);
            setIsGenerating(false);
            setProgress(0);
        }
    }, [inputValue, activeSession, sessionMessages, style, aspectRatio, contentFormat, sessions, saveSessions, currentBrandId, branchFromVersion, buildStructuredPrompt]);

    // Session handlers
    const handleOpenSession = useCallback((sessionId) => {
        const session = sessions.find(s => s.session_id === sessionId);
        if (session) {
            setActiveSession(session);
            setSessionMessages(session.messages || []);
            setActiveTab('chat');
        }
    }, [sessions]);

    const handleCloseSession = useCallback(() => {
        setActiveSession(null);
        setSessionMessages([]);
        setBranchFromVersion(null);
    }, []);

    const handleDeleteSession = useCallback((sessionId) => {
        if (!window.confirm('Delete this session?')) return;
        const updated = sessions.filter(s => s.session_id !== sessionId);
        saveSessions(updated);
        if (activeSession?.session_id === sessionId) {
            handleCloseSession();
        }
    }, [sessions, saveSessions, activeSession, handleCloseSession]);

    const handleDownload = useCallback((url, filename) => {
        downloadImage(url, filename);
    }, []);

    const handleUseInPost = useCallback((imageUrl) => {
        if (onUseInPostRequest) {
            const seed = buildPostComposerSeed({ result_url: imageUrl, caption: activeSession?.caption });
            savePostComposerSeed(seed);
            onUseInPostRequest(seed);
        }
    }, [activeSession, onUseInPostRequest]);

    const canSend = inputValue.trim().length > 0 && !isGenerating;

    return (
        <div className="h-full flex bg-slate-50 dark:bg-slate-900" style={{ minHeight: 0 }}>
            {/* Left Sidebar - Templates & Options */}
            <AnimatePresence initial={false}>
                {sidebarOpen && !activeSession && (
                    <motion.div
                        initial={{ width: 0, opacity: 0 }}
                        animate={{ width: 320, opacity: 1 }}
                        exit={{ width: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="flex-shrink-0 h-full border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"
                    >
                        <div className="h-full flex flex-col" style={{ minHeight: 0 }}>
                            {/* Sidebar Header */}
                            <div className="p-4 border-b border-slate-200 dark:border-slate-800">
                                <div className="flex items-center justify-between">
                                    <h2 className="font-semibold text-slate-800 dark:text-white">Templates</h2>
                                    <button
                                        onClick={() => setSidebarOpen(false)}
                                        className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-slate-400"
                                    >
                                        <PanelLeftClose className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            {/* Settings Section */}
                            <div className="p-4 border-b border-slate-200 dark:border-slate-800 space-y-3">
                                <div>
                                    <label className="block text-xs font-medium text-slate-500 mb-1.5">Content Format</label>
                                    <select
                                        value={contentFormat}
                                        onChange={(e) => setContentFormat(e.target.value)}
                                        className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm text-slate-800 dark:text-white border border-slate-200 dark:border-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                    >
                                        <option value="">Auto-detect</option>
                                        {CONTENT_FORMATS.map((f) => (
                                            <option key={f.id} value={f.id}>{f.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-medium text-slate-500 mb-1.5">Style</label>
                                        <select
                                            value={style}
                                            onChange={(e) => setStyle(e.target.value)}
                                            className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm text-slate-800 dark:text-white border border-slate-200 dark:border-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                        >
                                            {IMAGE_STYLES.map((s) => (
                                                <option key={s.id} value={s.id}>{s.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-slate-500 mb-1.5">Size</label>
                                        <select
                                            value={aspectRatio}
                                            onChange={(e) => setAspectRatio(e.target.value)}
                                            className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm text-slate-800 dark:text-white border border-slate-200 dark:border-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                        >
                                            {ASPECT_RATIOS.map((r) => (
                                                <option key={r.id} value={r.id}>{r.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* Prompt Boosters */}
                                <div>
                                    <label className="block text-xs font-medium text-slate-500 mb-1.5">Boost Prompt</label>
                                    <div className="flex flex-wrap gap-1.5">
                                        {PROMPT_BOOSTERS.map((b) => (
                                            <button
                                                key={b.id}
                                                onClick={() => toggleBooster(b.id)}
                                                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                                                    activeBoosters.includes(b.id)
                                                        ? 'bg-teal-500 text-white'
                                                        : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                                                }`}
                                            >
                                                {b.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Templates by Category */}
                            <div className="flex-1 overflow-y-auto" style={{ minHeight: 0 }}>
                                {Object.entries(groupedPresets).map(([category, presets]) => (
                                    <div key={category} className="border-b border-slate-100 dark:border-slate-800">
                                        <button
                                            onClick={() => setExpandedCategory(expandedCategory === category ? '' : category)}
                                            className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                                        >
                                            <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                                                {CATEGORY_LABELS[category] || category}
                                            </span>
                                            {expandedCategory === category ? (
                                                <ChevronDown className="w-4 h-4 text-slate-400" />
                                            ) : (
                                                <ChevronRight className="w-4 h-4 text-slate-400" />
                                            )}
                                        </button>
                                        <AnimatePresence>
                                            {expandedCategory === category && (
                                                <motion.div
                                                    initial={{ height: 0 }}
                                                    animate={{ height: 'auto' }}
                                                    exit={{ height: 0 }}
                                                    className="overflow-hidden"
                                                >
                                                    <div className="px-3 pb-3 space-y-2">
                                                        {presets.map((preset) => {
                                                            const FormatIcon = FORMAT_ICONS[preset.format] || Sparkles;
                                                            return (
                                                                <button
                                                                    key={preset.id}
                                                                    onClick={() => applyPreset(preset)}
                                                                    className="w-full p-3 text-left bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors group"
                                                                >
                                                                    <div className="flex items-start gap-3">
                                                                        <span className="text-lg">{preset.emoji}</span>
                                                                        <div className="flex-1 min-w-0">
                                                                            <p className="text-sm font-medium text-slate-800 dark:text-white truncate">
                                                                                {preset.name}
                                                                            </p>
                                                                            <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                                                                                {preset.description}
                                                                            </p>
                                                                        </div>
                                                                        <ChevronRight className="w-4 h-4 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5" />
                                                                    </div>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Main Chat Area */}
            <div className="flex-1 flex flex-col min-w-0" style={{ minHeight: 0 }}>
                {/* Header */}
                <div className="flex-shrink-0 px-4 py-3 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            {activeSession ? (
                                <button
                                    onClick={handleCloseSession}
                                    className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                                >
                                    <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                                </button>
                            ) : (
                                <>
                                    {!sidebarOpen && (
                                        <button
                                            onClick={() => setSidebarOpen(true)}
                                            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                                        >
                                            <PanelLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                                        </button>
                                    )}
                                    <div className="p-2 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-xl">
                                        <Sparkles className="w-5 h-5 text-white" />
                                    </div>
                                </>
                            )}
                            <div>
                                <h1 className="font-semibold text-slate-800 dark:text-white">
                                    {activeSession ? (activeSession.caption?.slice(0, 40) || 'Image Session') : 'AI Image Creator'}
                                </h1>
                                {activeSession && (
                                    <p className="text-xs text-slate-500">v{activeSession.current_version} · {getStyleById(activeSession.style)?.name}</p>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-slate-500 dark:text-slate-400">
                                <Sparkles className="w-4 h-4 inline mr-1 text-amber-500" />
                                {formatCredits(credits)}
                            </span>
                            {!activeSession && (
                                <>
                                    <button
                                        onClick={() => setActiveTab(activeTab === 'history' ? 'chat' : 'history')}
                                        className={`p-2 rounded-lg transition-colors ${activeTab === 'history' ? 'bg-teal-100 dark:bg-teal-900/30 text-teal-600' : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500'}`}
                                    >
                                        <History className="w-5 h-5" />
                                    </button>
                                    <button
                                        onClick={() => setActiveTab(activeTab === 'gallery' ? 'chat' : 'gallery')}
                                        className={`p-2 rounded-lg transition-colors ${activeTab === 'gallery' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600' : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500'}`}
                                    >
                                        <Crown className="w-5 h-5" />
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Error */}
                <AnimatePresence>
                    {error && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="px-4 py-2 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800"
                        >
                            <div className="flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
                                <AlertCircle className="w-4 h-4" />
                                <span className="flex-1">{error}</span>
                                <button onClick={() => setError(null)}><X className="w-4 h-4" /></button>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Main Content */}
                <div className="flex-1 overflow-hidden" style={{ minHeight: 0 }}>
                    {/* Chat View */}
                    {(activeTab === 'chat' || activeSession) && (
                        <div className="h-full flex flex-col" style={{ minHeight: 0 }}>
                            {/* Empty State - Centered Input */}
                            {sessionMessages.length === 0 && !activeSession ? (
                                <div className="flex-1 flex flex-col items-center justify-center p-4">
                                    <div className="w-full max-w-2xl">
                                        <div className="text-center mb-8">
                                            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center">
                                                <Sparkles className="w-8 h-8 text-white" />
                                            </div>
                                            <h2 className="text-2xl font-semibold text-slate-800 dark:text-white mb-2">
                                                Create Social Media Images
                                            </h2>
                                            <p className="text-slate-500 dark:text-slate-400 max-w-md mx-auto">
                                                Use templates from the sidebar or describe what you want
                                            </p>
                                        </div>

                                        {/* Centered Input Box */}
                                        <div className="relative">
                                            <textarea
                                                ref={inputRef}
                                                value={inputValue}
                                                onChange={(e) => setInputValue(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && !e.shiftKey) {
                                                        e.preventDefault();
                                                        handleSend();
                                                    }
                                                }}
                                                placeholder="Describe the image you want to create..."
                                                disabled={isGenerating}
                                                rows={4}
                                                className="w-full px-5 py-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-slate-800 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:opacity-50 resize-none text-base shadow-lg"
                                            />
                                            <div className="flex items-center justify-between mt-4">
                                                <p className="text-sm text-slate-400">
                                                    {CREDIT_COSTS.social_image} credits per generation
                                                </p>
                                                <button
                                                    onClick={handleSend}
                                                    disabled={!canSend}
                                                    className="px-6 py-2.5 bg-teal-500 hover:bg-teal-600 disabled:bg-slate-300 dark:disabled:bg-slate-700 text-white rounded-xl transition-colors disabled:cursor-not-allowed font-medium flex items-center gap-2"
                                                >
                                                    {isGenerating ? (
                                                        <Loader2 className="w-5 h-5 animate-spin" />
                                                    ) : (
                                                        <>
                                                            <Sparkles className="w-4 h-4" />
                                                            Generate
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                /* Chat with Messages */
                                <>
                                    <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-4" style={{ minHeight: 0 }}>
                                        <div className="max-w-3xl mx-auto space-y-6">
                                            <AnimatePresence>
                                                {sessionMessages.map((msg, idx) => (
                                                    <ChatMessage
                                                        key={idx}
                                                        message={msg}
                                                        onDownload={handleDownload}
                                                        onUseInPost={handleUseInPost}
                                                        onBranchFrom={activeSession?.status === SESSION_STATUS.ACTIVE ? setBranchFromVersion : null}
                                                    />
                                                ))}
                                            </AnimatePresence>

                                            {isGenerating && (
                                                <motion.div
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    className="flex gap-3"
                                                >
                                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center">
                                                        <Loader2 className="w-4 h-4 text-white animate-spin" />
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="inline-block px-4 py-3 bg-white dark:bg-slate-800 rounded-2xl shadow-sm">
                                                            <p className="text-sm text-slate-600 dark:text-slate-300">
                                                                Generating... {Math.round(progress)}%
                                                            </p>
                                                            <div className="mt-2 h-1 w-32 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                                                <div
                                                                    className="h-full bg-gradient-to-r from-teal-500 to-emerald-500 transition-all duration-300"
                                                                    style={{ width: `${progress}%` }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Bottom Input Area - for active conversations */}
                                    <div className="flex-shrink-0 p-4 pb-8 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
                                        <div className="max-w-3xl mx-auto">
                                            {branchFromVersion && (
                                                <div className="flex items-center gap-2 mb-2 px-3 py-1.5 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-sm">
                                                    <GitBranch className="w-4 h-4 text-amber-600" />
                                                    <span className="text-amber-700 dark:text-amber-400">Branching from v{branchFromVersion}</span>
                                                    <button onClick={() => setBranchFromVersion(null)} className="ml-auto text-amber-600"><X className="w-4 h-4" /></button>
                                                </div>
                                            )}
                                            <div className="flex gap-3">
                                                <div className="flex-1 relative">
                                                    <textarea
                                                        value={inputValue}
                                                        onChange={(e) => setInputValue(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                                e.preventDefault();
                                                                handleSend();
                                                            }
                                                        }}
                                                        placeholder="Describe changes or refinements..."
                                                        disabled={isGenerating}
                                                        rows={3}
                                                        className="w-full px-4 py-3 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-800 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:opacity-50 resize-none text-base"
                                                    />
                                                </div>
                                                <button
                                                    onClick={handleSend}
                                                    disabled={!canSend}
                                                    className="px-4 py-3 bg-teal-500 hover:bg-teal-600 disabled:bg-slate-300 dark:disabled:bg-slate-700 text-white rounded-xl transition-colors disabled:cursor-not-allowed self-end"
                                                >
                                                    {isGenerating ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                                                </button>
                                            </div>
                                            <p className="text-xs text-slate-400 text-center mt-2">
                                                {CREDIT_COSTS.social_image} credits per generation
                                            </p>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* History View */}
                    {activeTab === 'history' && !activeSession && (
                        <div className="h-full overflow-y-auto p-4">
                            <div className="max-w-4xl mx-auto">
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="text-lg font-semibold text-slate-800 dark:text-white">Recent Sessions</h2>
                                </div>
                                {sessions.length === 0 ? (
                                    <div className="text-center py-12">
                                        <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-300 dark:text-slate-600" />
                                        <p className="text-slate-500 dark:text-slate-400">No sessions yet</p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                        {sessions.map((session) => (
                                            <SessionCard
                                                key={session.session_id}
                                                session={session}
                                                onOpen={handleOpenSession}
                                                onDelete={handleDeleteSession}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Pro Gallery View */}
                    {activeTab === 'gallery' && !activeSession && (
                        <div className="h-full overflow-y-auto p-4">
                            <div className="max-w-4xl mx-auto">
                                <div className="text-center mb-8">
                                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full text-sm font-medium mb-4">
                                        <Crown className="w-4 h-4" />
                                        Professional Designer Service
                                    </div>
                                    <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-2">
                                        Hand-Crafted by Real Designers
                                    </h2>
                                    <p className="text-slate-500 dark:text-slate-400">24-hour delivery · Unlimited revisions</p>
                                </div>

                                <div className="flex justify-center gap-3 mb-8">
                                    {DESIGNER_PACKAGES.map((pkg) => (
                                        <div
                                            key={pkg.id}
                                            className={`px-4 py-3 rounded-xl text-center ${pkg.popular ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white' : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700'}`}
                                        >
                                            <p className="text-lg font-bold">${pkg.price}</p>
                                            <p className="text-sm opacity-80">{pkg.images} image{pkg.images > 1 ? 's' : ''}</p>
                                        </div>
                                    ))}
                                </div>

                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    {GALLERY_IMAGES.map((image) => (
                                        <motion.div
                                            key={image.id}
                                            whileHover={{ scale: 1.02 }}
                                            onClick={() => setSelectedGalleryImage(image)}
                                            className="aspect-square rounded-xl overflow-hidden cursor-pointer bg-slate-100 dark:bg-slate-800"
                                        >
                                            <img src={image.src} alt={image.title} className="w-full h-full object-cover" loading="lazy" />
                                        </motion.div>
                                    ))}
                                </div>

                                <div className="text-center mt-8">
                                    <button className="px-8 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all">
                                        Get Started - From $19.99
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Gallery Image Modal */}
            <AnimatePresence>
                {selectedGalleryImage && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
                        onClick={() => setSelectedGalleryImage(null)}
                    >
                        <motion.div
                            initial={{ scale: 0.9 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0.9 }}
                            className="relative max-w-2xl"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                onClick={() => setSelectedGalleryImage(null)}
                                className="absolute -top-10 right-0 p-2 text-white hover:text-slate-300"
                            >
                                <X className="w-6 h-6" />
                            </button>
                            <img
                                src={selectedGalleryImage.src}
                                alt={selectedGalleryImage.title}
                                className="w-full rounded-xl"
                            />
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

SocialImageGenerator.propTypes = {
    currentBrandId: PropTypes.string,
    onUseInPostRequest: PropTypes.func
};

export default SocialImageGenerator;
